import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Typography
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { getLocalizedString } from "@icarius-localization/strings";

const TabCompetencies = (props) => {
  const { editMode, currentNode, handleChangeField, skillList, color, theme, competencyGroupList, targetList } = props;

  const dispatch = useDispatch();
  const [selectedSkill, setSelectedSkill] = useState("");
  const [selectedTarget, setSelectedTarget] = useState("");

  const items = currentNode["skills"] || [];

  const handleAdd = () => {
    if (!selectedSkill || !selectedTarget) return;
    let skillObj = skillList.find(s => s.key === selectedSkill);
    if (!skillObj) return;

    // Si ya existe, lo rechaza
    if (items.find(item => item.key === selectedSkill || item.value === skillObj.value || item.code === selectedSkill)) {
      dispatch(openSnackbarAction({
        msg: getLocalizedString("competencyAlreadyExists"),
        severity: "warning"
      }));
      return;
    }

    handleChangeField("skills", [...items, { ...skillObj, target: selectedTarget }]);
    setSelectedSkill("");
    setSelectedTarget("");
  };

  const handleDelete = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    handleChangeField("skills", newItems);
  };

  const groupedItems = {};
  items.forEach((item, index) => {
    const groupKey = item.group || 'Transversal';
    if (!groupedItems[groupKey]) {
      groupedItems[groupKey] = [];
    }
    groupedItems[groupKey].push({ ...item, originalIndex: index });
  });

  // Ordenar elementos por tipo y luego por nombre
  Object.keys(groupedItems).forEach(groupKey => {
    groupedItems[groupKey].sort((a, b) => {
      if (a.type === "Específica" && b.type !== "Específica") return -1;
      if (a.type !== "Específica" && b.type === "Específica") return 1;
      return (a.value || "").localeCompare(b.value || "");
    });
  });

  const targetGroup = currentNode["Grupo de competencias específicas"] || currentNode["Nombre del cargo"];
  const filteredSkillList = skillList.filter(item =>
    item.group === targetGroup ||
    item.group === "Transversales"
  );

  return (
    <div style={{ padding: 16 }}>
      {
        editMode && (
          <Paper style={{ padding: 16, marginBottom: 16, minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center', backgroundColor: theme?.theme === "dark" ? '#1e1f20' : "#f0f4f9" }}>
            <Typography className="whiteText" variant="title" style={{ marginBottom: 16, fontWeight: "bold", fontSize: 20 }}>
              {currentNode["Grupo de competencias específicas"] ? getLocalizedString("competencyGroup") + ": " + currentNode["Grupo de competencias específicas"] : getLocalizedString("noCompetencyGroupDefined")}
            </Typography>
            <Typography className="whiteText" variant="subtitle1" style={{ marginBottom: 16 }}>
              {getLocalizedString("addCompetencies")}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel className="whiteText" id="skill-label">
                    {getLocalizedString("competence")}
                  </InputLabel>
                  <Select
                    labelId="skill-label"
                    value={selectedSkill}
                    onChange={(e) => setSelectedSkill(e.target.value)}
                    className="whiteText"
                  >
                    {
                      filteredSkillList.length > 0 ? (
                        filteredSkillList.map((item) => (
                          <MenuItem key={item.key} value={item.key}>
                            {item.value} ({item.type !== "Específica" ? getLocalizedString("transversalSingularCompetency") : getLocalizedString("specificSingularCompetency")})
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>{getLocalizedString("noCompetenciesForThisGroup")}</MenuItem>
                      )
                    }
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth required>
                  <InputLabel className="whiteText" id="target-label">
                    {getLocalizedString("targetSkillSpecific")}
                  </InputLabel>
                  <Select
                    labelId="target-label"
                    value={selectedTarget}
                    onChange={(e) => setSelectedTarget(e.target.value)}
                    className="whiteText"
                  >
                    {
                      targetList?.length > 0 ? (
                        targetList.map((item) => (
                          <MenuItem key={item.key} value={item.value}>
                            {item.value}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>{getLocalizedString("noTargets")}</MenuItem>
                      )
                    }
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={1}>
                <Button
                  variant="contained"
                  onClick={handleAdd}
                  disabled={!selectedSkill || !selectedTarget}
                  style={{ backgroundColor: color, marginTop: 12, minWidth: 'auto', padding: '6px 12px' }}
                >
                  <AddIcon />
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )
      }
      <div>
        {
          Object.keys(groupedItems).sort((a, b) => {
            if (a === "Transversales") return -1;
            if (b === "Transversales") return 1;
            return 0;
          }).map((groupKey) => {
            const isTransversal = groupKey === "Transversales";
            const groupTitle = isTransversal ? getLocalizedString("transversalCompetency") : competencyGroupList?.find(g => g.key === groupKey)?.value;
            return (
              <div key={groupKey}>
                <Typography variant="subtitle1" className="whiteText" style={{ marginTop: 16, marginBottom: 8, fontWeight: 'bold' }}>
                  {getLocalizedString(currentNode["Nombre del cargo"] === groupTitle ? "position" : "specificCompetencyGroup")}: {groupTitle}
                </Typography>
                <List>
                  {
                    groupedItems[groupKey].map((item) => {
                      return (
                        <ListItem key={item.originalIndex} className="listItemBorderBottom">
                          <ListItemText
                            primary={<span style={{ color: color, fontWeight: 600, fontSize: "1.1rem" }}>{isTransversal ? getLocalizedString("transversalSkill") : getLocalizedString("specificSkill")}</span>}
                            secondary={
                              <span className="whiteText" style={{ whiteSpace: 'pre-line', fontSize: "1rem" }}>
                                {getLocalizedString("name")}: <span style={{ textDecoration: "underline" }}>{item?.value}</span>
                                {"\n"}
                                {getLocalizedString("class")}: {item?.class}{"\n"}
                                {getLocalizedString("targetSkillSpecific")}: {item?.target}{"\n"}
                                {getLocalizedString("competencyOrigin")}: {getLocalizedString(currentNode["Nombre del cargo"] === groupTitle || isTransversal ? "assignedToPosition" : "competencyAssignedToGroup")}
                              </span>
                            }
                          />
                          {
                            editMode && (
                              <ListItemSecondaryAction>
                                <IconButton edge="end" onClick={() => handleDelete(item.originalIndex)} style={{ color: '#ff4d4f' }}>
                                  <DeleteIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            )
                          }
                        </ListItem>
                      )
                    })
                  }
                </List>
              </div>
            )
          })
        }
        {
          items.length === 0 && (
            <List>
              <Typography className="whiteText" style={{ textAlign: 'center', marginTop: 20, color: "var(--react-tel-arrow-color)" }}>
                {getLocalizedString("noCompetenciesAddedYet")}
              </Typography>
            </List>
          )
        }
      </div>
    </div>
  );
};

export default TabCompetencies;
